import { z } from 'zod';
import { ExpenseCategorySchema, ExpenseVatLineInputSchema } from './expenses';
import { TimeEntryPhaseSchema } from './time-entries';
/**
 * HOJA DE GASTOS — lo que una persona adelanta a lo largo de un mes.
 *
 * Importes en CÉNTIMOS. La hoja es un sobre de captura: mientras está en
 * BORRADOR no existe contablemente. Al APROBARLA, cada línea se convierte en un
 * gasto con su asiento.
 *
 * Tres tipos de línea, porque son tres cosas distintas:
 *   - TIQUE: hay un tercero y un papel. Lleva IVA deducible y va al libro
 *     registro de facturas recibidas.
 *   - KILOMETRAJE: km × tarifa. No hay tercero, no hay IVA.
 *   - DIETA: días × importe diario. Tampoco.
 *
 * Los dos últimos solo se pueden contabilizar a quien perciba rendimientos del
 * trabajo de JR2 (nómina o cargo de administrador). Ver `receivesExemptAllowances`.
 */
export const ExpenseSheetStatusSchema = z.enum([
    'BORRADOR',
    'ENVIADA',
    /** Contabilizándose ahora mismo; serializa dos aprobaciones simultáneas. */
    'APROBANDO',
    'APROBADA',
    'RECHAZADA',
]);
export const ExpenseSheetLineKindSchema = z.enum(['TIQUE', 'KILOMETRAJE', 'DIETA']);
// ---------- Entrada ----------
/**
 * Una línea. Los campos que se exigen dependen del tipo y se validan en el
 * `superRefine`: pedirlos todos siempre obligaría a inventar un proveedor para
 * un kilometraje, y admitirlos todos vacíos dejaría pasar una dieta sin días.
 */
export const ExpenseSheetLineInputSchema = z
    .object({
    /** id de una línea existente; ausente = línea nueva. */
    id: z.string().optional(),
    kind: ExpenseSheetLineKindSchema,
    /** Día del gasto o del desplazamiento (YYYY-MM-DD). */
    /**
     * YYYY-MM-DD. Con `min(10)` a secas, cualquier cadena de diez caracteres
     * llegaba a `new Date(...)` y salía Invalid Date sin que nadie se quejara.
     */
    lineDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    concept: z.string().min(1).max(300),
    category: ExpenseCategorySchema,
    opportunityId: z.string().nullable().optional(),
    phase: TimeEntryPhaseSchema.nullable().optional(),
    // TIQUE
    supplierName: z.string().max(200).nullable().optional(),
    supplierNif: z.string().max(32).nullable().optional(),
    invoiceNumber: z.string().max(64).nullable().optional(),
    vatLines: z.array(ExpenseVatLineInputSchema).optional(),
    // KILOMETRAJE
    km: z.number().positive().max(100000).nullable().optional(),
    originPlace: z.string().max(200).nullable().optional(),
    destinationPlace: z.string().max(200).nullable().optional(),
    vehiclePlate: z.string().max(20).nullable().optional(),
    // DIETA
    /**
     * UNA LÍNEA = UN DÍA (se admite 0,5 para medio día).
     *
     * El límite exento del art. 9.A.3 RIRPF es DIARIO por perceptor, así que
     * para comprobarlo hay que saber a qué día corresponde cada euro. Con una
     * línea de "3 días" y una sola fecha eso es indecidible, y dos líneas del
     * mismo día colaban el doble del límite como exento. Un viaje de tres días
     * son tres líneas, que además es como se justifica ante una inspección.
     */
    days: z.number().positive().max(1).nullable().optional(),
    overnight: z.boolean().optional(),
    abroad: z.boolean().optional(),
    /** Justificantes (ids de Document). Varios por línea. */
    documentIds: z.array(z.string()).optional(),
})
    .superRefine((v, ctx) => {
    if (v.kind === 'TIQUE') {
        if (!v.supplierName?.trim()) {
            ctx.addIssue({ code: 'custom', path: ['supplierName'], message: 'required' });
        }
        if (!v.vatLines || v.vatLines.length === 0) {
            ctx.addIssue({ code: 'custom', path: ['vatLines'], message: 'required' });
        }
    }
    if (v.kind === 'KILOMETRAJE') {
        if (v.km == null)
            ctx.addIssue({ code: 'custom', path: ['km'], message: 'required' });
        // El origen y el destino no son adorno: son lo que sostiene la exención
        // ante una inspección. Sin ellos la línea no vale como justificación.
        if (!v.originPlace?.trim()) {
            ctx.addIssue({ code: 'custom', path: ['originPlace'], message: 'required' });
        }
        if (!v.destinationPlace?.trim()) {
            ctx.addIssue({ code: 'custom', path: ['destinationPlace'], message: 'required' });
        }
    }
    if (v.kind === 'DIETA' && v.days == null) {
        ctx.addIssue({ code: 'custom', path: ['days'], message: 'required' });
    }
});
export const CreateExpenseSheetSchema = z.object({
    /** De quién es la hoja. Solo un ADMIN puede crearla a nombre de otro. */
    userId: z.string().optional(),
    year: z.number().int().min(2000).max(2100),
    month: z.number().int().min(1).max(12),
    notes: z.string().max(2000).nullable().optional(),
});
export const UpdateExpenseSheetSchema = z.object({
    notes: z.string().max(2000).nullable().optional(),
    /** Reemplaza el juego de líneas completo. Las que no vengan se borran. */
    lines: z.array(ExpenseSheetLineInputSchema).optional(),
});
export const RejectExpenseSheetSchema = z.object({
    reason: z.string().min(1).max(500),
});
export const ExpenseSheetListQuerySchema = z.object({
    year: z.coerce.number().int().min(2000).max(2100).optional(),
    month: z.coerce.number().int().min(1).max(12).optional(),
    userId: z.string().optional(),
    status: ExpenseSheetStatusSchema.optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
// ---------- Salida ----------
export const ExpenseSheetLineDtoSchema = z.object({
    id: z.string(),
    position: z.number().int(),
    kind: ExpenseSheetLineKindSchema,
    lineDate: z.string(),
    concept: z.string(),
    category: ExpenseCategorySchema,
    opportunityId: z.string().nullable(),
    opportunityCode: z.string().nullable(),
    phase: TimeEntryPhaseSchema.nullable(),
    supplierName: z.string().nullable(),
    supplierNif: z.string().nullable(),
    invoiceNumber: z.string().nullable(),
    vatLines: z.array(z.object({ vatRate: z.number(), subtotalCents: z.number().int(), vatCents: z.number().int() })),
    km: z.number().nullable(),
    ratePerKmCents: z.number().int().nullable(),
    originPlace: z.string().nullable(),
    destinationPlace: z.string().nullable(),
    vehiclePlate: z.string().nullable(),
    days: z.number().nullable(),
    overnight: z.boolean(),
    abroad: z.boolean(),
    perDiemCents: z.number().int().nullable(),
    subtotalCents: z.number().int(),
    vatCents: z.number().int(),
    totalCents: z.number().int(),
    exemptCents: z.number().int(),
    /** Exceso sobre el límite legal. Si es > 0, la hoja no se puede aprobar. */
    taxableCents: z.number().int(),
    /** Gasto generado al aprobar (null mientras es borrador). */
    expenseId: z.string().nullable(),
    expenseRef: z.string().nullable(),
    /**
     * El gasto se anuló (emitió su contra-asiento). La línea vuelve a estar por
     * materializar: sin este dato la pantalla la seguía enseñando bloqueada y el
     * usuario no tenía forma de saber que ya podía corregirla.
     */
    expenseVoided: z.boolean().default(false),
    /** Justificantes. `latestVersion` hace falta para construir el enlace de descarga. */
    documents: z.array(z.object({ id: z.string(), fileName: z.string(), latestVersion: z.number().int() })),
});
export const ExpenseSheetDtoSchema = z.object({
    id: z.string(),
    number: z.number().int(),
    year: z.number().int(),
    month: z.number().int(),
    /** "2026/4" — la referencia con la que se agrupan los asientos en el Diario. */
    ref: z.string(),
    userId: z.string(),
    userName: z.string(),
    status: ExpenseSheetStatusSchema,
    notes: z.string().nullable(),
    submittedAt: z.string().nullable(),
    approvedById: z.string().nullable(),
    approvedByName: z.string().nullable(),
    approvedAt: z.string().nullable(),
    rejectedReason: z.string().nullable(),
    /** Suma de las líneas. */
    totalCents: z.number().int(),
    /** Parte exenta (kilometraje y dietas dentro del límite). Va al modelo 190. */
    exemptCents: z.number().int(),
    /** Exceso sobre los límites legales; bloquea la aprobación mientras sea > 0. */
    taxableCents: z.number().int(),
    /**
     * Lo que la empresa TODAVÍA le debe a la persona por esta hoja. Se DERIVA de
     * la 551 (lo pendiente en los pagadores de los gastos generados), no se
     * guarda: un campo almacenado podría decir "reembolsada" mientras la cuenta
     * dice que no.
     */
    pendingReimbursementCents: z.number().int(),
    lines: z.array(ExpenseSheetLineDtoSchema),
    createdAt: z.string(),
});
export const ExpenseSheetListResponseSchema = z.object({
    items: z.array(ExpenseSheetDtoSchema),
    total: z.number().int(),
    page: z.number().int(),
    pageSize: z.number().int(),
});
/** Tarifas vigentes y sus límites legales, para que la pantalla calcule igual que el servidor. */
export const AllowanceRatesDtoSchema = z.object({
    mileageRateCents: z.number().int(),
    mileageExemptCents: z.number().int(),
    perDiem: z.object({
        nationalCents: z.number().int(),
        nationalOvernightCents: z.number().int(),
        abroadCents: z.number().int(),
        abroadOvernightCents: z.number().int(),
    }),
    perDiemExempt: z.object({
        nationalCents: z.number().int(),
        nationalOvernightCents: z.number().int(),
        abroadCents: z.number().int(),
        abroadOvernightCents: z.number().int(),
    }),
});
//# sourceMappingURL=expense-sheets.js.map